import React, { forwardRef } from 'react';
import { Box } from '@mui/material';
import { amountInWordsINR } from '../utils/numberToWords';
import { computeInvoiceTotals, dominantTaxRate } from '../utils/invoiceCalculations';

const BORDER = '#b8b8b8';
const HEADER_BG = '#e8e8e8';
const TEXT = '#000000';
const TEXT_BODY = '#333333';
const TEXT_TABLE = '#222222';
const TEXT_LIGHT = '#666666';

const PAGE_W = 794;
const PAGE_H = 1123;
const PAGE_MARGIN_SIDE = 36;
const PAGE_MARGIN_TOP = 48;

/** Same asset as Layout sidebar — hosted on admin deploy when not in local public/ */
const SWEAT_SOCIAL_LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;

const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

const fmtNum = (n: number) =>
  (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  invoice: any;
  captureId?: string;
}

/** Table column widths (%). Totals box spans tax + amount cols only — not tied to amount width alone. */
const COL = {
  num: 4,
  item: 22,
  qty: 11,
  rate: 12,
  cgstPct: 10,
  cgstAmt: 10,
  sgstPct: 10,
  sgstAmt: 10,
  amount: 11,
} as const;

/** Totals/signature box — starts at CGST Amt (inset slightly from full tax span) */
const TOTALS_BOX_W = COL.cgstAmt + COL.sgstPct + COL.sgstAmt + COL.amount;
const FOOTER_LEFT_W = 100 - TOTALS_BOX_W;

const cellBorder = `1px solid ${BORDER}`;

/** Single-side table lines — avoids doubled borders at cell edges */
const tableCellBorders = (lastCol = false, lastRow = false): React.CSSProperties => ({
  borderTop: 'none',
  borderLeft: 'none',
  borderRight: lastCol ? 'none' : cellBorder,
  borderBottom: lastRow ? 'none' : cellBorder,
});

const thStyle: React.CSSProperties = {
  ...tableCellBorders(),
  padding: '4px 5px',
  fontSize: 10,
  fontWeight: 700,
  textAlign: 'center',
  backgroundColor: HEADER_BG,
  color: TEXT,
  verticalAlign: 'middle',
};

const tdStyle = (lastCol = false, lastRow = false): React.CSSProperties => ({
  ...tableCellBorders(lastCol, lastRow),
  padding: '5px 5px',
  fontSize: 10,
  color: TEXT_TABLE,
  verticalAlign: 'middle',
  lineHeight: 1.3,
});

const InvoicePreview = forwardRef<HTMLDivElement, Props>(({ invoice, captureId }, ref) => {
  const sym = invoice?.currencySymbol || '₹';
  const logoBgColor = invoice?.logoBgColor || '#ffffff';
  const billFrom = invoice?.billFrom || {};
  const billTo = invoice?.billTo || {};
  const placeOfSupply = billFrom.placeOfSupply || billTo.placeOfSupply || '';
  const logoUrl = billFrom.logoUrl || '';

  const totals = computeInvoiceTotals(
    invoice?.lineItems || [],
    invoice?.discount || 0,
    invoice?.shipping || 0,
  );
  const { cgst: cgstRate, sgst: sgstRate } = dominantTaxRate(totals.lineItems);
  const dueDateStr = invoice?.dueDate ? fmtDate(invoice.dueDate) : fmtDate(invoice?.invoiceDate);

  const logoCell = (
    <Box
      sx={{
        width: 100,
        height: 80,
        flexShrink: 0,
        backgroundColor: logoUrl ? logoBgColor : HEADER_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <span style={{ fontSize: 28, color: TEXT_LIGHT }}>{(billFrom.name || 'S').charAt(0).toUpperCase()}</span>
      )}
    </Box>
  );

  return (
    <Box
      id={captureId}
      ref={ref}
      sx={{
        backgroundColor: '#fff',
        color: TEXT,
        width: PAGE_W,
        minHeight: PAGE_H,
        boxSizing: 'border-box',
        pt: `${PAGE_MARGIN_TOP}px`,
        px: `${PAGE_MARGIN_SIDE}px`,
        pb: `${PAGE_MARGIN_SIDE}px`,
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: 11,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ border: cellBorder, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Header — logo flush left, TAX INVOICE bottom-right.
            The logo is centred against the address block rather than bottom-
            aligned with it: the address runs to six or seven lines, so a
            bottom-aligned 80px logo sat low and looked dropped rather than
            paired with the studio name. */}
        <Box
          display="flex"
          alignItems="flex-end"
          sx={{ borderBottom: cellBorder }}
        >
          <Box sx={{ alignSelf: 'center' }}>{logoCell}</Box>
          <Box
            sx={{
              flex: 1,
              py: '16px',
              pl: '18px',
              pr: '18px',
              maxWidth: 320,
              fontSize: 10,
              lineHeight: 1.5,
              color: TEXT_BODY,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, color: TEXT, marginBottom: 3 }}>
              {billFrom.name || 'Your Company'}
            </div>
            {billFrom.companyId && <div style={{ marginBottom: 1 }}>Company ID : {billFrom.companyId}</div>}
            {billFrom.address && <div style={{ marginBottom: 1, whiteSpace: 'pre-line' }}>{billFrom.address}</div>}
            {billFrom.country && <div style={{ marginBottom: 1 }}>{billFrom.country}</div>}
            {billFrom.gstin && <div>GSTIN : {billFrom.gstin}</div>}
          </Box>
          <Box sx={{ flex: 1 }} />
          <div
            style={{
              fontSize: 36,
              fontWeight: 500,
              color: TEXT,
              flexShrink: 0,
              lineHeight: 1,
              paddingRight: 18,
              paddingBottom: 16,
              whiteSpace: 'nowrap',
            }}
          >
            TAX INVOICE
          </div>
        </Box>

        {/* Invoice metadata */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: cellBorder }}>
          <Box sx={{ p: '12px 18px', borderRight: cellBorder }}>
            <MetaRow label="#" value={invoice?.invoiceNumber || '—'} />
            <MetaRow label="Invoice Date" value={fmtDate(invoice?.invoiceDate)} />
            <MetaRow label="Terms" value="Due on Receipt" />
            <MetaRow label="Due Date" value={dueDateStr} last />
          </Box>
          <Box sx={{ p: '12px 18px' }}>
            <MetaRow label="Place Of Supply" value={placeOfSupply || '—'} last boldValue />
          </Box>
        </Box>

        {/* Bill To */}
        <Box sx={{ borderBottom: cellBorder }}>
          <Box
            sx={{
              backgroundColor: HEADER_BG,
              px: '14px',
              py: '5px',
              fontWeight: 700,
              fontSize: 11,
              color: TEXT,
              borderBottom: cellBorder,
            }}
          >
            Bill To
          </Box>
          <Box sx={{ px: '18px', py: '12px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: TEXT }}>{billTo.name || '—'}</div>
            {billTo.address && (
              <div style={{ fontSize: 10, color: TEXT_BODY, whiteSpace: 'pre-line', marginTop: 2 }}>
                {billTo.address}
              </div>
            )}
            {billTo.gstin && (
              <div style={{ fontSize: 10, color: TEXT_BODY, marginTop: 2 }}>GSTIN : {billTo.gstin}</div>
            )}
          </Box>
        </Box>

        {/* Line items */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 10,
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: `${COL.num}%` }} />
            <col style={{ width: `${COL.item}%` }} />
            <col style={{ width: `${COL.qty}%` }} />
            <col style={{ width: `${COL.rate}%` }} />
            <col style={{ width: `${COL.cgstPct}%` }} />
            <col style={{ width: `${COL.cgstAmt}%` }} />
            <col style={{ width: `${COL.sgstPct}%` }} />
            <col style={{ width: `${COL.sgstAmt}%` }} />
            <col style={{ width: `${COL.amount}%` }} />
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle} rowSpan={2}>#</th>
              <th style={{ ...thStyle, textAlign: 'left', paddingLeft: 8 }} rowSpan={2}>Item &amp; Description</th>
              <th style={thStyle} rowSpan={2}>Qty</th>
              <th style={thStyle} rowSpan={2}>Rate</th>
              <th style={thStyle} colSpan={2}>CGST</th>
              <th style={thStyle} colSpan={2}>SGST</th>
              <th style={{ ...thStyle, ...tableCellBorders(true) }} rowSpan={2}>Amount</th>
            </tr>
            <tr>
              <th style={thStyle}>%</th>
              <th style={thStyle}>Amt</th>
              <th style={thStyle}>%</th>
              <th style={thStyle}>Amt</th>
            </tr>
          </thead>
          <tbody>
            {totals.lineItems.map((it, i) => {
              const isLastRow = i === totals.lineItems.length - 1;
              return (
              <tr key={i}>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'center' }}>{i + 1}</td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'left', paddingLeft: 8 }}>
                  <div style={{ fontWeight: 600, color: TEXT }}>{it.description || '—'}</div>
                  {it.category && <div style={{ color: TEXT_LIGHT, fontSize: 9, marginTop: 1 }}>{it.category}</div>}
                </td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'right' }}>{(it.quantity || 0).toFixed(2)}</td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'right' }}>{fmtNum(it.rate)}</td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'center' }}>
                  {it.cgstPercent ? `${it.cgstPercent}%` : '—'}
                </td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'right' }}>{fmtNum(it.cgstAmt)}</td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'center' }}>
                  {it.sgstPercent ? `${it.sgstPercent}%` : '—'}
                </td>
                <td style={{ ...tdStyle(false, isLastRow), textAlign: 'right' }}>{fmtNum(it.sgstAmt)}</td>
                <td style={{ ...tdStyle(true, isLastRow), textAlign: 'right', fontWeight: 700, color: TEXT }}>
                  {fmtNum(it.amount)}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* Footer — grows to fill outer box height; single top border shared with table */}
        <Box display="flex" sx={{ flex: 1, minHeight: 0, borderTop: cellBorder }}>
          <Box sx={{ width: `${FOOTER_LEFT_W}%`, flexShrink: 0, p: '14px 18px', boxSizing: 'border-box' }}>
            <PlainLabel>Total In Words</PlainLabel>
            <div
              style={{
                fontStyle: 'italic',
                fontWeight: 700,
                fontSize: 11,
                color: TEXT,
                marginBottom: 12,
                lineHeight: 1.35,
              }}
            >
              {amountInWordsINR(totals.total)}
            </div>

            {invoice?.notes && (
              <Box sx={{ mb: 1.5 }}>
                <PlainLabel>Notes</PlainLabel>
                <div style={{ fontSize: 10, fontWeight: 400, color: TEXT_BODY, lineHeight: 1.4 }}>{invoice.notes}</div>
              </Box>
            )}

            {invoice?.terms && (
              <Box>
                <SectionLabel>Terms &amp; Conditions</SectionLabel>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 14,
                    fontSize: 9,
                    color: TEXT_BODY,
                    lineHeight: 1.45,
                    listStyleType: 'disc',
                  }}
                >
                  {String(invoice.terms)
                    .split('\n')
                    .map((line: string) => line.trim())
                    .filter(Boolean)
                    .map((line: string, idx: number) => (
                      <li key={idx} style={{ marginBottom: 2 }}>{line.replace(/^[•-]\s*/, '')}</li>
                    ))}
                </ul>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              width: `${TOTALS_BOX_W}%`,
              flexShrink: 0,
              alignSelf: 'flex-start',
              borderLeft: cellBorder,
              borderRight: cellBorder,
              borderBottom: cellBorder,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ p: '10px 14px', textAlign: 'right' }}>
              <TotalRow label="Sub Total" value={fmtNum(totals.subtotal)} />
              <TotalRow label={`CGST${cgstRate} (${cgstRate}%)`} value={fmtNum(totals.cgstTotal)} />
              <TotalRow label={`SGST${sgstRate} (${sgstRate}%)`} value={fmtNum(totals.sgstTotal)} />
              {invoice?.discount ? <TotalRow label="Discount" value={`- ${fmtNum(invoice.discount)}`} /> : null}
              {invoice?.shipping ? <TotalRow label="Shipping" value={fmtNum(invoice.shipping)} /> : null}
              <TotalRow label="Total" value={`${sym}${fmtNum(totals.total)}`} bold />
            </Box>
            <Box
              sx={{
                borderTop: cellBorder,
                minHeight: 100,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pb: '10px',
              }}
            >
              <span style={{ fontSize: 9, fontWeight: 400, color: TEXT_LIGHT }}>
                Authorized Signature
              </span>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Page footer */}
      <Box
        sx={{
          mt: '8px',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 9,
          color: TEXT_LIGHT,
        }}
      >
        <Box display="flex" alignItems="center" gap="8px">
          <span style={{ fontWeight: 700, fontSize: 9, color: TEXT }}>POWERED BY</span>
          <img
            src={SWEAT_SOCIAL_LOGO}
            alt="SweatSocial"
            style={{ height: 20, objectFit: 'contain' }}
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.dataset.fallback) {
                img.dataset.fallback = '1';
                img.src = `${window.location.origin}${process.env.PUBLIC_URL || ''}/logo.png`;
              }
            }}
          />
        </Box>
        <span style={{ fontSize: 10 }}>1</span>
      </Box>
    </Box>
  );
});

const MetaRow: React.FC<{ label: string; value: string; last?: boolean; boldValue?: boolean }> = ({
  label, value, last, boldValue,
}) => (
  <Box display="flex" sx={{ mb: last ? 0 : '3px', fontSize: 10, lineHeight: 1.45, color: TEXT_BODY }}>
    <span style={{ minWidth: 100, flexShrink: 0 }}>{label}</span>
    <span style={{ marginRight: 5 }}>:</span>
    <span style={{ fontWeight: boldValue ? 700 : 400, color: TEXT }}>{value}</span>
  </Box>
);

const PlainLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontWeight: 400, fontSize: 11, color: TEXT_BODY, marginBottom: 3 }}>{children}</div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontWeight: 700, fontSize: 11, color: TEXT, marginBottom: 3 }}>{children}</div>
);

const TotalRow: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <Box
    display="flex"
    justifyContent="flex-end"
    gap="24px"
    sx={{
      py: '1px',
      fontSize: bold ? 11 : 10,
      fontWeight: bold ? 700 : 400,
      color: bold ? TEXT : TEXT_BODY,
      lineHeight: 1.35,
    }}
  >
    <span>{label}</span>
    <span style={{ color: TEXT, minWidth: 72, textAlign: 'right', fontWeight: bold ? 700 : 400 }}>{value}</span>
  </Box>
);

InvoicePreview.displayName = 'InvoicePreview';

export default InvoicePreview;
